/**
 * Boekhouding Service - API calls voor boekhouding
 * Morris-style structuur
 */

import apiFetch from './api';

// Jaar opties - gedeelde constante voor dropdown lijsten
export const BOEKJAAR_START = 2026;
export const BOEKJAAR_EIND = 2030;
export const BOEKJAAR_OPTIES = Array.from(
  { length: BOEKJAAR_EIND - BOEKJAAR_START + 1 },
  (_, i) => BOEKJAAR_START + i
);

// Enums - Morris lowercase style
export enum GrootboekType {
  OMZET = 'omzet',
  INKOOP = 'inkoop',
  KOSTEN = 'kosten',
  BALANS = 'balans',
}

export enum BoekstukType {
  KAS = 'kas',
  BANK = 'bank',
  INKOOP = 'inkoop',
  VERKOOP = 'verkoop',
  MEMORIAAL = 'memoriaal',
  LENING = 'lening',
}

// Interfaces - Morris structuur
export interface Grootboekrekening {
  grootboeknaam: string;  // Primary key in Morris
  grootboek_nr?: string;
  type: GrootboekType;
  btw_percentage?: number;
  actief: boolean;
  created_at?: string;
}

export interface GrootboekrekeningCreate {
  grootboeknaam: string;
  grootboek_nr?: string;
  type: GrootboekType;
  btw_percentage?: number;
  actief?: boolean;
}

export interface GrootboekrekeningListResponse {
  items: Grootboekrekening[];
  total: number;
}

export interface Boeking {
  id: number;
  jaar: number;
  datum: string;
  boekstuk: BoekstukType;
  boekstuknummer?: string;
  omschrijving: string;
  bedrag_excl: number;
  btw_percentage: number;
  btw_bedrag: number;
  bedrag_incl: number;
  grootboeknaam: string;  // Morris uses grootboeknaam as FK
  relatie_id?: number;
  relatie_naam?: string;  // Joined from relatie table
  notitie?: string;
  is_credit: boolean;
  status?: string;
  gekoppeld_aan_id?: number;
  extern_factuurnummer?: string;
  created_at?: string;
  // Morris: saldo en betaald berekend uit betalingen tabel
  betaald?: number;
  saldo?: number;
  // Morris: document info
  heeft_document?: boolean;
  document_type?: string;
}

export interface BoekingCreate {
  jaar: number;
  datum: string;
  boekstuk: BoekstukType;
  boekstuknummer?: string;
  omschrijving: string;
  bedrag_excl: number;
  btw_percentage?: number;
  btw_bedrag: number;
  bedrag_incl: number;
  grootboeknaam: string;
  relatie_id?: number;
  notitie?: string;
  is_credit?: boolean;
  status?: string;
  extern_factuurnummer?: string;
  gekoppeld_aan_id?: number;   // betaling op een inkoop- of verkoopboeking
}

export interface BoekingListResponse {
  items: Boeking[];
  total: number;
  page: number;
  page_size: number;
}

export interface BoekingFilters {
  skip?: number;
  limit?: number;
  jaar?: number;
  boekstuk?: BoekstukType;
  zoek?: string;
}

export interface BTWKwartaal {
  kwartaal: number;
  omzet_hoog: number;
  btw_hoog: number;
  omzet_laag: number;
  btw_laag: number;
  voorbelasting: number;
  te_betalen: number;
}

// Auto data voor BTW privégebruik
export interface AutoData {
  id: number;
  naam: string;
  catalogusprijs: number;
  bouwjaar: number;
  start_datum: string | null;
  eind_datum: string | null;
  percentage_override: number | null;
}

// Morris BTW Rubriek Data - voor ingediend en werkelijk
export interface BTWRubriekData {
  r1a_omzet: number;
  r1a_btw: number;
  r1b_omzet: number;
  r1b_btw: number;
  r1c_omzet: number;
  r1c_btw: number;
  r1d_omzet: number;
  r1d_btw: number;
  r1e_omzet: number;
  r2a_omzet: number;
  r2a_btw: number;
  r3a_omzet: number;
  r3b_omzet: number;
  r3c_omzet: number;
  r4a_omzet: number;
  r4a_btw: number;
  r4b_omzet: number;
  r4b_btw: number;
  r5b_voorbelasting: number;
  r5d_kor: number;
  betaald: number;
}

export interface BTWSuppletie {
  kwartaal: number;
  verschil: number;
  datum?: string;
}

export interface BTWSuppleties {
  suppleties: BTWSuppletie[];
  totaal: number;
}

export interface BTWVerschil {
  kwartaal: number;
  ingediend: number;
  werkelijk: number;
  verschil: number;
}

// Morris BTW Overzicht Response - EXACT Morris structuur
export interface BTWOverzicht {
  jaar: number;
  kwartaal: number;
  werkelijk: BTWRubriekData;
  ingediend: BTWRubriekData;
  suppleties: BTWSuppleties;
  btw_verschillen: BTWVerschil[];
}

export interface BTWAangifteCreate {
  jaar: number;
  kwartaal: number;
  r1a_omzet: number;
  r1a_btw: number;
  r1b_omzet: number;
  r1b_btw: number;
  r1c_omzet: number;
  r1c_btw: number;
  r1d_omzet: number;
  r1d_btw: number;
  r1e_omzet: number;
  r2a_omzet: number;
  r2a_btw: number;
  r3a_omzet: number;
  r3b_omzet: number;
  r3c_omzet: number;
  r4a_omzet: number;
  r4a_btw: number;
  r4b_omzet: number;
  r4b_btw: number;
  r5b_voorbelasting: number;
  r5d_kor: number;
  betaald: number;
}

export interface BTWSuppletieCreate {
  jaar: number;
  kwartaal: number;
  verschil: number;
  ingediend_datum: string;
}

// Balans - EXACT Morris data structuur
export interface BalansAuto {
  id: number;
  naam: string;
  catalogusprijs: number;
  bouwjaar: number;
  start_datum: string | null;
  eind_datum: string | null;
  percentage_override: number | null;
}

export interface BalansBeginbalans {
  jaar: number;
  activa: Record<string, number>;
  passiva: Record<string, number>;
}

export interface Balans {
  jaar: number;
  peildatum: string;  // YYYY-MM-DD - EXACT Morris
  banknaam: string;   // naam van de bank uit de bedrijfsgegevens
  activa: {
    bank: number;
    kas: number;
    vaste_activa: Record<string, number>;
    debiteuren: number;
    btw_vordering: number;
    totaal: number;
  };
  passiva: {
    lening_ford_transit: number;
    crediteuren: number;
    prive_km: number;
    btw_schuld: number;
    extra_btw_auto: number;
    oudedagreserve: number;
    kapitaal: number;
    totaal: number;
  };
  verschil: number;
  beginbalans: BalansBeginbalans;
  autos: BalansAuto[];
}

// Exploitatie - EXACT Morris data structuur met kwartalen
export interface ExploitatieKostenRegel {
  naam: string;
  totaal: number;
  kwartalen: number[];  // [Q1, Q2, Q3, Q4]
}

export interface Exploitatie {
  jaar: number;
  omzet: {
    totaal: number;
    kwartalen: number[];  // [Q1, Q2, Q3, Q4]
  };
  kosten: ExploitatieKostenRegel[];
  kosten_totaal: {
    totaal: number;
    kwartalen: number[];
  };
  winst: {
    totaal: number;
    kwartalen: number[];
  };
}

// Activa - Morris style
export interface Activa {
  id: number;
  omschrijving: string;
  categorie: string;
  aanschafdatum: string;
  aanschafwaarde: number;
  afschrijvingspercentage: number;
  restwaarde: number;
  actief: boolean;
  created_at?: string;
}

export interface ActivaCreate {
  omschrijving: string;
  categorie: string;
  aanschafdatum: string;
  aanschafwaarde: number;
  afschrijvingspercentage: number;
  restwaarde?: number;
  actief?: boolean;
}

export interface ActivaListResponse {
  items: Activa[];
  total: number;
}

export interface BoekhoudingInstelling {
  key: string;
  jaar: number;
  value?: string;
}

// Dashboard - Morris style
export interface DashboardKwartaal {
  kwartaal: number;
  inkomsten: number;
  uitgaven: number;
  resultaat: number;
}

export interface DashboardData {
  jaar: number;
  totaal_inkomsten: number;
  totaal_uitgaven: number;
  resultaat: number;
  btw_te_betalen: number;
  btw_begin_saldo?: number;
  btw_verkoop?: number;
  btw_voorbelasting?: number;
  btw_prive_auto?: number;
  btw_betaald?: number;
  bank_saldo: number;
  kas_saldo: number;
  kwartalen: DashboardKwartaal[];
}

// Leningen
export interface Lening {
  id: number;
  datum: string;
  boekstuknummer: number;
  omschrijving: string;
  credit: number | null;
  debet: number | null;
}

export interface LeningVerdichting {
  naam: string;
  credit: number;
  debet: number;
  saldo: number;
}

export interface LeningenData {
  jaar: number;
  leningen: Lening[];
  verdichting: LeningVerdichting[];
  totaal_credit: number;
  totaal_debet: number;
  eind_saldo: number;
}

// Debiteuren & Crediteuren - EXACT Morris style
export interface DCDebiteurTotaal {
  begin_saldo: number;
  verkoop: number;
  bank_ontvangen: number;
  kas_ontvangen: number;
  openstaand: number;
}

export interface DCCrediteurTotaal {
  begin_saldo: number;
  inkoop: number;
  bank_betaald: number;
  kas_betaald: number;
  openstaand: number;
}

export interface DCDebiteurKwartaal {
  kwartaal: number;
  verkoop: number;
  bank_ontvangen: number;
  kas_ontvangen: number;
}

export interface DCCrediteurKwartaal {
  kwartaal: number;
  inkoop: number;
  bank_betaald: number;
  kas_betaald: number;
}

export interface DCOpenstaandeFactuur {
  id: number;
  datum: string;
  boekstuknummer: string | null;
  omschrijving: string;
  bedrag: number;
  betaald: number;
  openstaand: number;
}

export interface DCData {
  jaar: number;
  totaal: {
    debiteuren: DCDebiteurTotaal;
    crediteuren: DCCrediteurTotaal;
  };
  kwartalen: {
    debiteuren: DCDebiteurKwartaal[];
    crediteuren: DCCrediteurKwartaal[];
  };
  openstaande_debiteuren: DCOpenstaandeFactuur[];
  openstaande_crediteuren: DCOpenstaandeFactuur[];
}

// Kapitaal
export interface KapitaalData {
  jaar: number;
  begin_saldo: number;
  winst: number;
  prive_opnames_netto: number;
  or_dotatie: number;
  eind_saldo: number;
  oudedagreserve: number;
  eigen_vermogen: number;
}

// Afschrijvingen
export interface AfschrijvingItem {
  id: number;
  omschrijving: string;
  categorie: string;
  aanschafdatum: string;
  datum: string;  // Morris format: dd-mm-yy
  aanschafwaarde: number;
  aantal_jaar: number;
  restwaarde: number;
  dag_tarief: number;
  afgeschreven: number;
  begin_boekwaarde: number;
  afschrijving: number;
  eind_boekwaarde: number;
  is_vast?: boolean;
}

export interface AfschrijvingenData {
  jaar: number;
  activa_per_categorie: { [key: string]: AfschrijvingItem[] };
  per_categorie: { [key: string]: { begin_boekwaarde: number; afschrijving: number; eind_boekwaarde: number } };
  totaal: { begin_boekwaarde: number; afschrijving: number; eind_boekwaarde: number };
}

// Helper functie om lege strings te verwijderen uit data
const cleanBoekingData = (data: any): any => {
  const cleaned: any = {};
  for (const key in data) {
    const value = data[key];
    if (value === '' && (key === 'datum' || key.includes('datum'))) {
      continue;
    }
    if (value === '' || value === null || value === undefined) {
      continue;
    }
    cleaned[key] = value;
  }
  return cleaned;
};

export const boekhoudingService = {
  // Grootboekrekeningen
  async getGrootboekrekeningen(filters: { actief?: boolean; type?: GrootboekType } = {}): Promise<GrootboekrekeningListResponse> {
    const params = new URLSearchParams();
    if (filters.actief !== undefined) params.append('actief', filters.actief.toString());
    if (filters.type) params.append('type', filters.type);

    return apiFetch(`/api/v1/boekhouding/grootboek?${params.toString()}`);
  },

  async getGrootboekrekening(id: number): Promise<Grootboekrekening> {
    return apiFetch(`/api/v1/boekhouding/grootboek/${id}`);
  },

  async createGrootboekrekening(data: GrootboekrekeningCreate): Promise<Grootboekrekening> {
    return apiFetch('/api/v1/boekhouding/grootboek', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateGrootboekrekening(id: number, data: Partial<GrootboekrekeningCreate>): Promise<Grootboekrekening> {
    return apiFetch(`/api/v1/boekhouding/grootboek/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Boekingen
  async getBoekingen(filters: BoekingFilters = {}): Promise<BoekingListResponse> {
    const params = new URLSearchParams();
    if (filters.skip !== undefined) params.append('skip', filters.skip.toString());
    if (filters.limit !== undefined) params.append('limit', filters.limit.toString());
    if (filters.jaar !== undefined) params.append('jaar', filters.jaar.toString());
    if (filters.boekstuk) params.append('boekstuk', filters.boekstuk);
    if (filters.zoek) params.append('zoek', filters.zoek);

    return apiFetch(`/api/v1/boekhouding/boekingen?${params.toString()}`);
  },

  async getBoeking(id: number): Promise<Boeking> {
    return apiFetch(`/api/v1/boekhouding/boekingen/${id}`);
  },

  async createBoeking(data: BoekingCreate): Promise<Boeking> {
    const cleanedData = cleanBoekingData(data);
    return apiFetch('/api/v1/boekhouding/boekingen', {
      method: 'POST',
      body: JSON.stringify(cleanedData),
    });
  },

  async updateBoeking(id: number, data: Partial<BoekingCreate>): Promise<Boeking> {
    const cleanedData = cleanBoekingData(data);
    return apiFetch(`/api/v1/boekhouding/boekingen/${id}`, {
      method: 'PUT',
      body: JSON.stringify(cleanedData),
    });
  },

  async deleteBoeking(id: number): Promise<void> {
    return apiFetch(`/api/v1/boekhouding/boekingen/${id}`, {
      method: 'DELETE',
    });
  },

  async getVolgendBoekstuknummer(jaar: number, boekstuk: BoekstukType): Promise<{ volgend_nummer: number }> {
    const params = new URLSearchParams();
    params.append('jaar', jaar.toString());
    params.append('boekstuk', boekstuk);
    return apiFetch(`/api/v1/boekhouding/volgend-boekstuknummer?${params.toString()}`);
  },

  // BTW
  async getBTWOverzicht(jaar: number, kwartaal: number = 1): Promise<BTWOverzicht> {
    return apiFetch(`/api/v1/boekhouding/btw-overzicht?jaar=${jaar}&kwartaal=${kwartaal}`);
  },

  async saveBTWAangifte(data: BTWAangifteCreate): Promise<{status: string}> {
    return apiFetch('/api/v1/boekhouding/btw-aangifte', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  async saveBTWSuppletie(data: BTWSuppletieCreate): Promise<{status: string}> {
    return apiFetch('/api/v1/boekhouding/btw-suppletie', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  async deleteBTWSuppletie(jaar: number, kwartaal: number): Promise<{status: string}> {
    return apiFetch(`/api/v1/boekhouding/btw-suppletie?jaar=${jaar}&kwartaal=${kwartaal}`, {
      method: 'DELETE',
    });
  },

  // Balans & Exploitatie - EXACT Morris met peildatum ondersteuning
  async getBalans(jaar: number, peildatum?: string): Promise<Balans> {
    let url = `/api/v1/boekhouding/balans?jaar=${jaar}`;
    if (peildatum) {
      url += `&peildatum=${peildatum}`;
    }
    return apiFetch(url);
  },

  async getExploitatie(jaar: number): Promise<Exploitatie> {
    return apiFetch(`/api/v1/boekhouding/exploitatie?jaar=${jaar}`);
  },

  // Activa
  async getActiva(filters: { actief?: boolean } = {}): Promise<ActivaListResponse> {
    const params = new URLSearchParams();
    if (filters.actief !== undefined) params.append('actief', filters.actief.toString());
    return apiFetch(`/api/v1/boekhouding/activa?${params.toString()}`);
  },

  async createActiva(data: ActivaCreate): Promise<Activa> {
    const cleanedData = cleanBoekingData(data);
    return apiFetch('/api/v1/boekhouding/activa', {
      method: 'POST',
      body: JSON.stringify(cleanedData),
    });
  },

  // Instellingen
  async getInstellingen(): Promise<BoekhoudingInstelling[]> {
    const response = await apiFetch('/api/v1/boekhouding/instellingen') as { items: BoekhoudingInstelling[] };
    return response.items;
  },

  async updateInstelling(key: string, value: string): Promise<BoekhoudingInstelling> {
    return apiFetch(`/api/v1/boekhouding/instellingen/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value }),
    });
  },

  // Autos voor BTW privégebruik
  async getAutos(): Promise<AutoData[]> {
    return apiFetch('/api/v1/boekhouding/autos');
  },

  async createAuto(data: { naam: string; catalogusprijs: number; bouwjaar: number; percentage_override?: number }): Promise<{ status: string }> {
    const formData = new FormData();
    formData.append('naam', data.naam);
    formData.append('catalogusprijs', data.catalogusprijs.toString());
    formData.append('bouwjaar', data.bouwjaar.toString());
    if (data.percentage_override) {
      formData.append('percentage_override', data.percentage_override.toString());
    }
    return apiFetch('/api/v1/boekhouding/autos', {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    });
  },

  async deleteAuto(id: number): Promise<{ status: string }> {
    return apiFetch(`/api/v1/boekhouding/autos/${id}`, {
      method: 'DELETE',
    });
  },

  async updateAuto(id: number, data: { naam: string; catalogusprijs: number; bouwjaar: number; percentage_override?: number; start_datum?: string; eind_datum?: string }): Promise<{ status: string }> {
    const formData = new FormData();
    formData.append('naam', data.naam);
    formData.append('catalogusprijs', data.catalogusprijs.toString());
    formData.append('bouwjaar', data.bouwjaar.toString());
    if (data.percentage_override) {
      formData.append('percentage_override', data.percentage_override.toString());
    }
    if (data.start_datum) {
      formData.append('start_datum', data.start_datum);
    }
    if (data.eind_datum) {
      formData.append('eind_datum', data.eind_datum);
    }
    return apiFetch(`/api/v1/boekhouding/autos/${id}`, {
      method: 'PUT',
      body: formData,
      headers: {},
    });
  },

  // Dashboard
  async getDashboard(jaar: number): Promise<DashboardData> {
    return apiFetch(`/api/v1/boekhouding/dashboard?jaar=${jaar}`);
  },

  // Leningen
  async getLeningen(jaar: number): Promise<LeningenData> {
    return apiFetch(`/api/v1/boekhouding/leningen?jaar=${jaar}`);
  },

  // Debiteuren & Crediteuren
  async getDebiteurenCrediteuren(jaar: number): Promise<DCData> {
    return apiFetch(`/api/v1/boekhouding/debiteuren-crediteuren?jaar=${jaar}`);
  },

  // Kapitaal
  async getKapitaal(jaar: number): Promise<KapitaalData> {
    return apiFetch(`/api/v1/boekhouding/kapitaal?jaar=${jaar}`);
  },

  // Afschrijvingen
  async getAfschrijvingen(jaar: number): Promise<AfschrijvingenData> {
    return apiFetch(`/api/v1/boekhouding/afschrijvingen?jaar=${jaar}`);
  },

  // Jaarrekening - complete data voor alle 12 pagina's
  async getJaarrekening(jaar: number): Promise<JaarrekeningData> {
    return apiFetch(`/api/v1/boekhouding/jaarrekening?jaar=${jaar}`);
  },

  // Beginbalans - EXACT Morris structuur
  async getBeginbalans(jaar: number): Promise<BeginbalansData> {
    return apiFetch(`/api/v1/boekhouding/beginbalans?jaar=${jaar}`);
  },

  async createBeginbalansItem(data: BeginbalansItemCreate): Promise<{ status: string; message: string }> {
    return apiFetch('/api/v1/boekhouding/beginbalans/nieuw', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateBeginbalansItem(id: number, bedrag: number): Promise<{ status: string; message: string }> {
    return apiFetch(`/api/v1/boekhouding/beginbalans/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ bedrag }),
    });
  },

  async deleteBeginbalansItem(id: number): Promise<{ status: string; message: string }> {
    return apiFetch(`/api/v1/boekhouding/beginbalans/${id}`, {
      method: 'DELETE',
    });
  },

  async ophalenVorigJaar(jaar: number): Promise<{ status: string; message: string }> {
    return apiFetch('/api/v1/boekhouding/beginbalans/ophalen-vorig-jaar', {
      method: 'POST',
      body: JSON.stringify({ jaar }),
    });
  },

  // Activa bewerken
  async updateActiva(id: number, data: {
    omschrijving?: string;
    categorie?: string;
    aanschafdatum?: string;
    aanschafwaarde?: number;
    afschrijvingspercentage?: number;
    restwaarde?: number;
  }): Promise<AfschrijvingItem> {
    return apiFetch(`/api/v1/boekhouding/activa/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  // Activa verwijderen
  async deleteActiva(id: number): Promise<void> {
    return apiFetch(`/api/v1/boekhouding/activa/${id}`, {
      method: 'DELETE',
    });
  },

  // Relaties - EXACT Morris
  async getRelaties(filters: { type?: string; search?: string } = {}): Promise<RelatieListResponse> {
    const params = new URLSearchParams();
    if (filters.type) params.append('type', filters.type);
    if (filters.search) params.append('search', filters.search);
    const queryString = params.toString();
    return apiFetch(`/api/v1/boekhouding/relaties${queryString ? `?${queryString}` : ''}`);
  },

  async getRelatie(id: number): Promise<Relatie> {
    return apiFetch(`/api/v1/boekhouding/relaties/${id}`);
  },

  async createRelatie(data: RelatieCreate): Promise<Relatie> {
    return apiFetch('/api/v1/boekhouding/relaties', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  async updateRelatie(id: number, data: RelatieUpdate): Promise<Relatie> {
    return apiFetch(`/api/v1/boekhouding/relaties/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  async deleteRelatie(id: number): Promise<void> {
    return apiFetch(`/api/v1/boekhouding/relaties/${id}`, {
      method: 'DELETE',
    });
  },

  // ==================== AI OCR (Morris) ====================

  async scanFactuur(
    file: File,
    boekstuk: 'inkoop' | 'verkoop' = 'inkoop',
    perspectivePoints?: number[]
  ): Promise<OCRResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('boekstuk', boekstuk);
    if (perspectivePoints) {
      formData.append('perspective_points', JSON.stringify(perspectivePoints));
    }

    const token = localStorage.getItem('access_token');
    const apiBaseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:7077'
      : window.location.hostname.startsWith('192.168.')
      ? `http://${window.location.hostname}:7077`
      : '';

    const response = await fetch(`${apiBaseUrl}/api/v1/boekhouding/ocr`, {
      method: 'POST',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'OCR request failed' }));
      throw new Error(error.detail || 'OCR request failed');
    }

    return response.json();
  },

  async testApiKey(apiKey: string): Promise<{ success: boolean; message: string }> {
    const formData = new FormData();
    formData.append('api_key', apiKey);

    const token = localStorage.getItem('access_token');
    const apiBaseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:7077'
      : window.location.hostname.startsWith('192.168.')
      ? `http://${window.location.hostname}:7077`
      : '';

    const response = await fetch(`${apiBaseUrl}/api/v1/boekhouding/test-api-key`, {
      method: 'POST',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: formData,
    });

    return response.json();
  },

  async zoekBedrijfsgegevens(naam: string): Promise<{ success: boolean; message?: string; data?: any }> {
    const formData = new FormData();
    formData.append('naam', naam);

    const token = localStorage.getItem('access_token');
    const apiBaseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:7077'
      : window.location.hostname.startsWith('192.168.')
      ? `http://${window.location.hostname}:7077`
      : '';

    const response = await fetch(`${apiBaseUrl}/api/v1/boekhouding/bedrijf-zoeken`, {
      method: 'POST',
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: formData,
    });

    return response.json();
  },
};

// Jaarrekening types - Morris layout
export interface JaarrekeningBedrijf {
  naam_regel1: string;
  naam_regel2: string;
  naam: string;
  adres: string;
  postcode: string;
  plaats: string;
}

export interface JaarrekeningExploitatie {
  omzet: { totaal: number; kwartalen: number[] };
  kosten: { naam: string; totaal: number; kwartalen: number[] }[];
  kosten_totaal: number;
  winst: number;
}

export interface JaarrekeningBalansActiva {
  bank: number;
  kas: number;
  debiteuren: number;
  btw_vordering: number;
  vaste_activa: { [key: string]: number };
  totaal: number;
}

export interface JaarrekeningBalansPassiva {
  kapitaal: number;
  oudedagreserve: number;
  lening: number;
  crediteuren: number;
  prive_km: number;
  btw_schuld: number;
  btw_prive_auto: number;
  totaal: number;
}

export interface JaarrekeningBalans {
  banknaam: string;
  activa: JaarrekeningBalansActiva;
  passiva: JaarrekeningBalansPassiva;
}

export interface JaarrekeningBeginbalans {
  activa: { [key: string]: number };
  passiva: { [key: string]: number };
}

export interface JaarrekeningKapitaal {
  begin_saldo: number;
  winst: number;
  prive_opnames: number;
  prive_stortingen: number;
  prive_opnames_netto: number;
  or_dotatie: number;
  or_begin: number;
  or_eind: number;
  eind_saldo: number;
  oudedagreserve: number;
  eigen_vermogen: number;
  ondernemingsvermogen: number;
  totaal_onttrekkingen: number;
}

export interface JaarrekeningActivaItem {
  id: number;
  omschrijving: string;
  categorie: string;
  datum: string;
  aanschafwaarde: number;
  aantal_jaar: number;
  restwaarde: number;
  dag_tarief: number;
  afgeschreven: number;
  begin_boekwaarde: number;
  afschrijving: number;
  eind_boekwaarde: number;
}

export interface JaarrekeningAfschrijvingen {
  activa: JaarrekeningActivaItem[];
  per_categorie: { [key: string]: { begin_boekwaarde: number; afschrijving: number; eind_boekwaarde: number } };
  totaal: { begin_boekwaarde: number; afschrijving: number; eind_boekwaarde: number };
}

export interface JaarrekeningBTW {
  kwartalen: BTWKwartaal[];
  totaal_te_betalen: number;
}

export interface JaarrekeningData {
  jaar: number;
  bedrijf: JaarrekeningBedrijf;
  exploitatie: JaarrekeningExploitatie;
  balans: JaarrekeningBalans;
  beginbalans: JaarrekeningBeginbalans;
  kapitaal: JaarrekeningKapitaal;
  afschrijvingen: JaarrekeningAfschrijvingen;
  btw: JaarrekeningBTW;
}

// Beginbalans - EXACT Morris structuur
export interface BeginbalansItem {
  id: number;
  jaar: number;
  categorie: string;
  omschrijving: string;
  bedrag: number;
  type: 'activa' | 'passiva';
}

export interface BeginbalansData {
  jaar: number;
  activa: { [categorie: string]: BeginbalansItem[] };
  passiva: { [categorie: string]: BeginbalansItem[] };
  totaal_activa: number;
  totaal_passiva: number;
}

export interface BeginbalansItemCreate {
  jaar: number;
  categorie: string;
  omschrijving: string;
  bedrag: number;
  type: 'activa' | 'passiva';
}

// Relaties - EXACT Morris structuur
export enum RelatieType {
  DEBITEUR = 'debiteur',
  CREDITEUR = 'crediteur',
  BEIDE = 'beide',
}

export interface Relatie {
  id: number;
  naam: string;
  type: RelatieType;
  adres?: string;
  postcode?: string;
  plaats?: string;
  email?: string;
  telefoon?: string;
  btw_nummer?: string;
  kvk_nummer?: string;
}

export interface RelatieCreate {
  naam: string;
  type: RelatieType;
  adres?: string;
  postcode?: string;
  plaats?: string;
  email?: string;
  telefoon?: string;
  btw_nummer?: string;
  kvk_nummer?: string;
}

export interface RelatieUpdate {
  naam?: string;
  type?: RelatieType;
  adres?: string;
  postcode?: string;
  plaats?: string;
  email?: string;
  telefoon?: string;
  btw_nummer?: string;
  kvk_nummer?: string;
}

export interface RelatieListResponse {
  items: Relatie[];
  total: number;
}

// OCR Response - Morris structuur
export interface OCRData {
  leverancier?: string;
  klant?: string;
  factuurnummer?: string;
  datum?: string;
  bedrag_excl?: number;
  btw_bedrag?: number;
  bedrag_incl?: number;
  btw_percentage?: number;
  grootboek?: string;
  omschrijving?: string;
  kvk_nummer?: string;
  btw_nummer?: string;
  adres?: string;
  postcode?: string;
  plaats?: string;
  email?: string;
  telefoon?: string;
}

export interface OCRRelatieMatch {
  id: number;
  naam: string;
  match_score?: number;
}

export interface OCRFactuurBestaat {
  id: number;
  boekstuknummer: string;
  factuurnummer?: string;
  datum: string;
  bedrag: number;
  is_placeholder?: boolean;
}

export interface OCRNieuweRelatieSuggestie {
  naam: string;
  kvk_nummer?: string;
  btw_nummer?: string;
  adres?: string;
  postcode?: string;
  plaats?: string;
  email?: string;
  telefoon?: string;
}

export interface OCRResponse {
  success: boolean;
  message?: string;
  data?: OCRData;
  auto_cropped?: boolean;
  crop_message?: string;
  cropped_image?: string;  // base64
  relatie_match?: OCRRelatieMatch;
  grootboek_suggestie?: string;
  factuur_bestaat?: OCRFactuurBestaat;
  placeholder_match?: OCRFactuurBestaat;
  nieuwe_relatie_suggestie?: OCRNieuweRelatieSuggestie;
}
